import { Popover } from '@mantine/core'
import { DatePicker as BaseDatePicker } from '@mantine/dates'
import React, { useState } from 'react'
import isEqual from 'react-fast-compare'
import { format, isValid, parse } from 'date-fns'

import styles from './DatePicker.module.scss'
import { TextInput } from '@percent/lemonade'
import { DatePickerProps } from './DatePicker.types'

const DATE_FORMAT = 'MM/dd/yyyy'

export const DatePicker = React.memo(({ name, onChange, onBlur, value, placeholder }: DatePickerProps) => {
  const [isOpen, setIsOpen] = useState(false)
  // Tracks typing independently of form state; calendar picks sync it below.
  const [inputText, setInputText] = useState(() => (value ? format(value, DATE_FORMAT) : ''))

  const handleClose = () => {
    setIsOpen(false)
  }

  const handleOpen = () => {
    setIsOpen(true)
  }

  const handleTextChange = (text: string) => {
    setInputText(text)
    // Partial or invalid input → null form state so Next stays disabled.
    const parsed = text.length === DATE_FORMAT.length ? parse(text, DATE_FORMAT, new Date()) : null

    if (parsed && isValid(parsed) && parsed <= new Date()) {
      onChange(parsed)
    } else {
      onChange(null)
    }
  }

  const handleCalendarPick = (date: Date | null) => {
    onChange(date)
    setInputText(date ? format(date, DATE_FORMAT) : '')
    setIsOpen(false)
  }

  return (
    <Popover
      opened={isOpen}
      position="bottom-start"
      shadow="md"
      onDismiss={handleClose}
      onClose={handleClose}
      onOpen={handleOpen}
    >
      <Popover.Target>
        <TextInput
          className={styles.DateInput}
          onClick={handleOpen}
          onChange={event => handleTextChange(event.target.value)}
          onBlur={onBlur}
          value={inputText}
          name={name}
          placeholder={placeholder}
          autoComplete="off"
        />
      </Popover.Target>
      <Popover.Dropdown>
        <div className={styles.DatePicker}>
          <BaseDatePicker value={value} maxDate={new Date()} onChange={handleCalendarPick} numberOfColumns={1} />
        </div>
      </Popover.Dropdown>
    </Popover>
  )
}, isEqual)
